/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import React, {Component} from 'react';
import {surveyData} from './SurveyData';
import Radio from '@react/react-spectrum/Radio';
import Textfield from '@react/react-spectrum/Textfield';
import Textarea from '@react/react-spectrum/Textarea';
import Button from '@react/react-spectrum/Button';
import Close from '@react/react-spectrum/Icon/Close';
import {Toast} from '@react/react-spectrum/Toast';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import SurveyPopupAnalytics from './Analytics/SurveyPopupAnalytics';

import './SurveyPopup.scss';
interface Props {
	title: string;
	showJarvis: Function;
}

interface State {
	answered: boolean;
	currentTab: number;
	open: boolean;
	selectedIndex: number;
	userResponse: string;
	showToast: boolean;
	mounted: boolean;
	hidden: boolean;
	survey: Object;
	responseContext: Array<string>;
}
class SurveyPopup extends Component<Props, State> {
	state = {
		currentTab: 0,
		open: true,
		selectedIndex: -1,
		userResponse: '',
		showToast: false,
		mounted: false,
		hidden: true,
		survey: surveyData[this.props.title],
		responseContext: [],
		answered: false,
	};

	componentDidMount() {
		const analytics = new SurveyPopupAnalytics();
		analytics.postRender();

		setTimeout(() => {
			this.setState({hidden: false});
		}, 5000);
		this.setState({mounted: true});
	}

	componentWillUnmount() {
		const userID = window.cchome.util.getUserId();
		if (
			this.state.answered
			&& window.cchome.util.storageAvailable('localStorage')
			&& !localStorage.getItem(`closedSurveyPopup-${userID}`)
		) {
			this.recordUserCloseSurvey();
		}
		this.props.showJarvis();
	}

	sendClickEventAnalytics(
		eventSubtype: string,
		contentName: string,
		contextGUID: Array<string>,
		eventValue: string,
		sortOrder: number,
	) {
		const analytics = new SurveyPopupAnalytics();
		analytics.postClickAction(
			eventSubtype,
			contentName.split(',').join('-')
				.split(' ')
				.join('-'),
			contextGUID.join('&&&'),
			eventValue,
			sortOrder,
		);
	}

	skipNextPage() {
		const {currentTab, selectedIndex, survey} = this.state;
		if (survey[currentTab].answers[selectedIndex] === 'To manage my account or subscription') {
			return false;
		}
		return true;
	}

	handleOnClick = (e) => {
		// send user response
		const {currentTab, selectedIndex, userResponse, survey, responseContext} = this.state;
		let userFinalChoice;
		if (selectedIndex === -1 || survey[currentTab].answers[selectedIndex] === 'None of the above') {
			// short/long answer only or user selects "none of the above"
			userFinalChoice = userResponse;
		}
		else {
			// multiple choice answer
			userFinalChoice = survey[currentTab].answers[selectedIndex];
		}

		const newResponseContext = [...responseContext, userFinalChoice];

		this.sendClickEventAnalytics(
			'question',
			survey[currentTab].subject,
			newResponseContext,
			userFinalChoice,
			currentTab + 1,
		);

		if (currentTab === survey.length - 1) {
			this.recordUserCloseSurvey();
			this.setState(
				{
					open: false,
					showToast: true,
					mounted: false,
					currentTab: currentTab + 1,
					responseContext: newResponseContext,
				},
				() => {
					// send click analytics event
					setTimeout(() => {
						this.closeToast();
					}, 2000);
				},
			);
		}
		else {
			const numOfPagesToAdvance = this.skipNextPage() && currentTab === 1 ? 2 : 1;

			this.setState({
				currentTab: currentTab + numOfPagesToAdvance,
				selectedIndex: -1,
				responseContext: newResponseContext,
				userResponse: '',
				answered: true,
			});
		}
	};

	onSelect = (idx) => {
		this.setState(
			{
				selectedIndex: idx,
				userResponse: '',
			},
			() =>
				document.getElementsByClassName('submit-button')[0]
				&& (document.getElementsByClassName('submit-button')[0] as HTMLElement).focus(),
		);
	};

	handleUserTypedResponse = (v) => {
		this.setState({userResponse: v});
	};

	closeSurvey = (e) => {
		this.recordUserCloseSurvey();

		const {currentTab, survey, responseContext} = this.state;
		// send dismiss analytics event
		this.sendClickEventAnalytics('dismiss', survey[currentTab].subject, responseContext, '', currentTab + 1);
		this.setState({open: false}, () => this.props.showJarvis());
	};

	recordUserCloseSurvey() {
		if (window.cchome.util.storageAvailable('localStorage')) {
			const userID = window.cchome.util.getUserId();
			localStorage.setItem(`closedSurveyPopup-${userID}`, 'true');
		}
	}

	closeToast = () => {
		this.setState({showToast: false}, () => this.props.showJarvis());
	};

	checkButton = () => {
		const {currentTab, selectedIndex, userResponse, survey} = this.state;
		if (selectedIndex !== -1 && survey[currentTab].answers[selectedIndex] !== 'None of the above') {
			return false;
		}
		else if (userResponse !== '') {
			return false;
		}
		return true;
	};

	checkShortAnswer = () => {
		const {currentTab, selectedIndex, survey} = this.state;
		return survey[currentTab].answers[selectedIndex] !== 'None of the above';
	};

	renderSurveyContent() {
		const {selectedIndex, userResponse, currentTab, survey} = this.state;
		const {subject, answers, shortAnswer, warning, longAnswer} = survey[currentTab];
		const lastTab = currentTab === survey.length - 1;
		const disableButton = this.checkButton();
		const disableShortAnswer = this.checkShortAnswer();

		return (
			<div className='survey-component-content'>
				<h3>{subject}</h3>
				<hr />
				{warning && <p className='warning'>{warning}</p>}
				{!!answers.length
					&& answers.map((q, i) => (
						<Radio
							key={i}
							label={q}
							onChange={() => this.onSelect(i)}
							checked={i === selectedIndex}
							className='multiple-choice'
						/>
					))}
				{shortAnswer && (
					<Textfield
						placeholder={shortAnswer}
						onChange={this.handleUserTypedResponse}
						value={userResponse}
						disabled={disableShortAnswer}
						className='short-answer'
						maxLength='128'
					/>
				)}
				{longAnswer && (
					<Textarea
						rows='7'
						placeholder={longAnswer}
						onChange={this.handleUserTypedResponse}
						className='long-answer'
						maxLength='280'
					/>
				)}
				{/* <span className='page-number'>{currentTab+1}&nbsp;/&nbsp;{survey.length}</span> */}
				<Button
					label={lastTab ? 'Submit' : 'Next'}
					variant='cta'
					onClick={this.handleOnClick}
					disabled={disableButton}
					className='submit-button'
				/>
			</div>
		);
	}

	renderSurvey() {
		const surveyStyle = this.state.hidden
			? {}
			: ({position: 'fixed', right: '40px', bottom: '60px'} as React.CSSProperties);
		if (this.state.open) {
			return (
				<CSSTransition timeout={500} classNames='fade' key={this.state.currentTab}>
					<div className='survey-component-container' style={surveyStyle}>
						<Button
							icon={<Close size='S' />}
							onClick={this.closeSurvey}
							variant='tool'
							className='survey-component-close-button'
						/>
						<div className='survey-component-wrapper'>{this.renderSurveyContent()}</div>
					</div>
				</CSSTransition>
			);
		}
		return null;
	}

	renderToast() {
		return (
			<CSSTransition timeout={500} classNames='fade' key={this.state.currentTab}>
				<Toast variant='success' closable onClose={this.closeToast} className='survey-submit-toast'>
					Thank you for your response
				</Toast>
			</CSSTransition>
		);
	}

	render() {
		const {showToast} = this.state;

		return <TransitionGroup>{showToast ? this.renderToast() : this.renderSurvey()}</TransitionGroup>;
	}
}

export default SurveyPopup;
